import { usePaginationControls } from '@common/hooks/usePaginationControls';
import { DeliveryModal } from '@features/delivery/components/DeliveryModal';
import { useDeliveryContext } from '@features/delivery/components/DeliveryModal/DeliveryContext';
import { FormRenderer } from '@features/dynamicForm/components/FormRenderer';
import { Badge, Button, Flex, ScrollArea } from '@radix-ui/themes';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MediaItemLabel } from '@common/components/MediaItemLabel';
import { useServiceOrderContext } from '../context/ServiceOrderContext';
import { useCoreOrderConfigs, useSharedOrderConfigs } from '../hooks';
import {
    BaseOrderPayload,
    OrderDeliveryItem,
    OrderType,
    WizardAccumulatedData,
} from '../types';

export const ServiceOrderDialog = () => {
    const { t } = useTranslation('serviceOrders');
    const { selectedOrderType } = useServiceOrderContext();
    const { isOpen, executeAction, isProcessing, validItems, selectedIds } =
        useDeliveryContext<OrderDeliveryItem, BaseOrderPayload>();

    const [wizardData, setWizardData] = useState<WizardAccumulatedData>({});
    const [currentStepIndex, setCurrentStepIndex] = useState(0);

    const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
    if (isOpen !== prevIsOpen) {
        setPrevIsOpen(isOpen);
        if (!isOpen) {
            setWizardData({});
            setCurrentStepIndex(0);
        }
    }

    const activeOrderType: OrderType | undefined =
        wizardData.orderType ??
        (selectedOrderType !== null ? selectedOrderType : undefined);

    const { baseConfig, finalConfig } = useSharedOrderConfigs();
    const coreConfigs = useCoreOrderConfigs(activeOrderType);

    const activeSteps = useMemo(() => {
        return [baseConfig, ...coreConfigs, finalConfig];
    }, [baseConfig, coreConfigs, finalConfig]);

    const pagination = usePaginationControls({
        itemCount: activeSteps.length,
        pageIndex: currentStepIndex,
        pageSize: 1,
        pageSizes: [1],
        onPaginationChange: (newPagination) => {
            setCurrentStepIndex(newPagination.pageIndex);
        },
    });

    const activeConfig = activeSteps[pagination.pageIndex];
    const showItemSummary = selectedIds.length > 0 && pagination.pageIndex > 0;

    const handleNextOrSubmit = (stepData: WizardAccumulatedData) => {
        let accumulatedData = { ...wizardData, ...stepData };

        if (stepData.orderType && stepData.orderType !== wizardData.orderType) {
            accumulatedData = {
                orderType: stepData.orderType,
                references: stepData.references,
            };
        }

        setWizardData(accumulatedData);

        if (!pagination.hasNextPage) {
            executeAction(accumulatedData as BaseOrderPayload);
        } else {
            pagination.nextPage();
        }
    };

    const showItemList = validItems.length > 0 && pagination.pageIndex === 0;

    return (
        <ScrollArea type='scroll' scrollbars='vertical' asChild>
            <DeliveryModal.Content<OrderDeliveryItem, BaseOrderPayload>
                title={
                    activeConfig
                        ? t(activeConfig.title)
                        : t('orders.fallbackTitle')
                }
                a11yDescription={t('orders.a11yDescription')}
                successTitle={t('orders.success.title')}
                successDescription={t('orders.success.desc')}
                maxWidth='800px'
                maxHeight={'calc(100vh - 50px)'}
                minHeight={'350px'}
            >
                <Flex
                    direction='column'
                    gap='4'
                    maxHeight='calc(100vh - 250px)'
                    // style={{ minHeight: 0 }}
                    minHeight={'350px'}
                >
                    {showItemList && (
                        <>
                            <DeliveryModal.InvalidCallout
                                warningText={t('warnings.invalidItems')}
                            />
                            <DeliveryModal.ItemList
                                label={t('inputs.items')}
                                renderItem={(item) => (
                                    <MediaItemLabel
                                        name={item.name}
                                        hasProxy={item.hasProxy}
                                    />
                                )}
                            />
                        </>
                    )}

                    {showItemSummary && (
                        <Flex justify={'start'}>
                            <Badge
                                size={'2'}
                                color={'gray'}
                                variant={'surface'}
                                radius={'full'}
                            >
                                {t('inputs.items')} : {selectedIds.length}
                            </Badge>
                        </Flex>
                    )}

                    {activeConfig && (
                        <FormRenderer<WizardAccumulatedData>
                            key={`wizard-step-${pagination.pageIndex}`}
                            config={activeConfig}
                            onSubmit={handleNextOrSubmit}
                            defaultValues={wizardData}
                            footer={
                                <>
                                    {pagination.hasPreviousPage && (
                                        <Button
                                            type='button'
                                            variant='soft'
                                            color='gray'
                                            onClick={pagination.previousPage}
                                            disabled={isProcessing}
                                        >
                                            {t('buttons.back')}
                                        </Button>
                                    )}

                                    <Button
                                        type='submit'
                                        size='3'
                                        disabled={isProcessing}
                                        style={{
                                            cursor: isProcessing
                                                ? 'wait'
                                                : 'pointer',
                                        }}
                                    >
                                        {isProcessing
                                            ? t('buttons.submitting')
                                            : !pagination.hasNextPage
                                              ? t('buttons.submit')
                                              : t('buttons.next')}
                                    </Button>
                                </>
                            }
                        />
                    )}
                </Flex>
            </DeliveryModal.Content>
        </ScrollArea>
    );
};
